import { createStore } from "vuex";
import apiService from "@/utils/api";

export default createStore({
  state: {
    loading: false,
    error: null,
    darkMode: false,
    isAuthenticated: false,
    user: null,
    token: null,
    isInitializing: false,
    expirationCheckInterval: null,
    flags: {},
    /** Por id de flag: true cuando ya terminó el GET (éxito o error) */
    flagsLoadFinished: {},
    /** Canal para lead.source en consultas: app | vitality (viene del JWT user_session.leadChannel) */
    leadChannel: null,
  },
  mutations: {
    SET_LOADING(state, loading) {
      state.loading = loading;
    },
    SET_ERROR(state, error) {
      state.error = error;
    },
    TOGGLE_DARK_MODE(state) {
      state.darkMode = !state.darkMode;
    },
    SET_AUTHENTICATED(state, isAuthenticated) {
      state.isAuthenticated = isAuthenticated;
    },
    SET_USER(state, user) {
      state.user = user;
    },
    SET_TOKEN(state, token) {
      state.token = token;
      // Store token in localStorage for persistence
      if (token) {
        localStorage.setItem("authToken", token);
      } else {
        localStorage.removeItem("authToken");
      }
    },
    UPDATE_USER_DATA(state, updates) {
      if (state.user) {
        state.user = { ...state.user, ...updates };
      }
    },
    SET_INITIALIZING(state, initializing) {
      state.isInitializing = initializing;
    },
    SET_EXPIRATION_CHECK_INTERVAL(state, intervalId) {
      state.expirationCheckInterval = intervalId;
    },
    SET_FLAG(state, { id, data }) {
      state.flags = { ...(state.flags || {}), [id]: data };
    },
    FLAG_LOAD_FINISHED(state, { id }) {
      if (!id) return;
      state.flagsLoadFinished = {
        ...(state.flagsLoadFinished || {}),
        [id]: true,
      };
    },
    RESET_FLAGS(state) {
      state.flags = {};
      state.flagsLoadFinished = {};
    },
    SET_LEAD_CHANNEL(state, leadChannel) {
      state.leadChannel = leadChannel;
    },
  },
  actions: {
    setLoading({ commit }, loading) {
      commit("SET_LOADING", loading);
    },
    setError({ commit }, error) {
      commit("SET_ERROR", error);
    },
    async loginWithToken({ commit, dispatch }, token) {
      try {
        commit("SET_LOADING", true);
        commit("SET_ERROR", null);

        // Store the token
        commit("SET_TOKEN", token);

        // Decode JWT token to get user info
        const userData = await dispatch("decodeToken", token);

        const lc = userData.leadChannel;
        commit(
          "SET_LEAD_CHANNEL",
          lc === "vitality" || lc === "app" ? lc : "app",
        );

        // Get full user profile from backend
        const userProfile = await dispatch("fetchUserProfile", {
          token,
          userId: userData.userId,
        });

        // Set authentication state
        commit("SET_AUTHENTICATED", true);

        // Use API data if available, otherwise use token data
        const finalUserData = userProfile.user || userData;

        // If API failed but token is valid, still proceed with authentication
        if (!userProfile.user && userProfile.error) {
          console.warn(
            "API unavailable, proceeding with token-only authentication:",
            userProfile.error,
          );
        }

        commit("SET_USER", finalUserData);

        dispatch("loadFlag", { id: "roulette", token });

        return finalUserData;
      } catch (error) {
        console.error("Error in loginWithToken:", error);
        commit("SET_ERROR", "Token validation failed");
        commit("SET_AUTHENTICATED", false);
        commit("SET_TOKEN", null);
        throw error;
      } finally {
        commit("SET_LOADING", false);
      }
    },
    async fetchUserProfile(_, { token, userId }) {
      try {
        const response = await apiService.getUserProfile(token, userId);
        return response;
      } catch (error) {
        console.error("Error fetching user profile:", error);
        // If API fails, return the token data as fallback
        console.log("Using token data as fallback for user profile");
        return { user: null, error: error.message };
      }
    },
    async updateUserProfile(_, { token, userId, profileData }) {
      try {
        const response = await apiService.updateUserProfile(
          token,
          userId,
          profileData,
        );
        return response;
      } catch (error) {
        console.error("Error updating user profile:", error);
        throw error;
      }
    },
    async decodeToken(_, token) {
      try {
        // Validate token format first
        if (!token || typeof token !== "string") {
          throw new Error("Token no proporcionado");
        }

        // Check if token has the expected JWT format (3 parts separated by dots)
        const tokenParts = token.split(".");
        if (tokenParts.length !== 3) {
          throw new Error("Formato de token inválido - debe tener 3 partes");
        }

        // Basic JWT decode (without verification - for demo purposes)
        const base64Url = tokenParts[1];
        if (!base64Url) {
          throw new Error(
            "Formato de token inválido - parte del payload faltante",
          );
        }

        const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
        const jsonPayload = decodeURIComponent(
          atob(base64)
            .split("")
            .map(function (c) {
              return "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2);
            })
            .join(""),
        );

        const decodedData = JSON.parse(jsonPayload);

        // Check if token is expired
        if (decodedData.exp) {
          const currentTime = Math.floor(Date.now() / 1000);
          if (currentTime > decodedData.exp) {
            console.log(
              "Token is expired. Current time:",
              currentTime,
              "Expiration:",
              decodedData.exp,
            );
            throw new Error("Token expirado");
          }
          console.log(
            "Token es válido. Expira en:",
            new Date(decodedData.exp * 1000),
          );
        }

        return decodedData;
      } catch (error) {
        console.error("Token decode error:", error);
        // Return a more specific error message
        if (error.message.includes("Token expirado")) {
          throw new Error("Token expirado");
        } else if (error.message.includes("Formato de token inválido")) {
          throw new Error("Formato de token inválido");
        } else {
          throw new Error("Token inválido o corrupto");
        }
      }
    },
    async login({ commit }, userData) {
      commit("SET_AUTHENTICATED", true);
      commit("SET_USER", userData);
    },
    logout({ commit, state }) {
      // Clear expiration check interval
      if (state.expirationCheckInterval) {
        clearInterval(state.expirationCheckInterval);
        commit("SET_EXPIRATION_CHECK_INTERVAL", null);
      }

      commit("SET_AUTHENTICATED", false);
      commit("SET_USER", null);
      commit("SET_TOKEN", null);
      commit("SET_LEAD_CHANNEL", null);
      commit("RESET_FLAGS");
    },
    // Initialize auth state from localStorage
    async initializeAuth({ commit, dispatch, state }) {
      // Prevent duplicate initialization calls
      if (state.isInitializing) {
        return false;
      }

      commit("SET_INITIALIZING", true);

      const token = localStorage.getItem("authToken");

      try {
        if (token) {
          const userData = await dispatch("decodeToken", token);

          if (!userData || !userData.userId) {
            throw new Error("Invalid token data - missing userId");
          }

          const lc = userData.leadChannel;
          commit(
            "SET_LEAD_CHANNEL",
            lc === "vitality" || lc === "app" ? lc : "app",
          );

          // Fetch fresh user profile
          const userProfile = await dispatch("fetchUserProfile", {
            token,
            userId: userData.userId,
          });

          commit("SET_TOKEN", token);
          commit("SET_AUTHENTICATED", true);

          // Use API data if available, otherwise use token data
          const finalUserData = userProfile.user || userData;

          // If API failed but token is valid, still proceed with authentication
          if (!userProfile.user && userProfile.error) {
            console.warn(
              "API unavailable during initialization, proceeding with token-only authentication:",
              userProfile.error,
            );
          }

          commit("SET_USER", finalUserData);

          dispatch("loadFlag", { id: "roulette", token });

          // Start periodic token expiration check
          dispatch("startTokenExpirationCheck", userData.exp);

          return true;
        } else {
          return false;
        }
      } catch (error) {
        console.error("Error initializing auth from localStorage:", error);
        // Token is invalid, clear it
        localStorage.removeItem("authToken");
        commit("SET_AUTHENTICATED", false);
        commit("SET_USER", null);
        commit("SET_TOKEN", null);
        commit("SET_LEAD_CHANNEL", null);
        commit("RESET_FLAGS");
        // Set session invalid flag to show appropriate message
        sessionStorage.setItem("sessionInvalid", "true");
        // Don't re-throw the error to prevent unhandled promise rejection
        return false;
      } finally {
        commit("SET_INITIALIZING", false);
      }
    },

    async loadFlag({ commit }, { id, token }) {
      if (!id || !token) {
        if (id) commit("FLAG_LOAD_FINISHED", { id });
        return null;
      }
      try {
        const res = await apiService.getFlag(token, id);
        if (res?.code === 200 && res.data) {
          commit("SET_FLAG", { id, data: res.data });
          return res.data;
        }
        commit("SET_FLAG", { id, data: { id, roles: [] } });
        return null;
      } catch (e) {
        console.warn("loadFlag failed:", id, e?.message || e);
        commit("SET_FLAG", { id, data: { id, roles: [] } });
        return null;
      } finally {
        commit("FLAG_LOAD_FINISHED", { id });
      }
    },

    // Start periodic token expiration check
    startTokenExpirationCheck({ commit, dispatch }, expirationTime) {
      if (!expirationTime) return;

      const checkExpiration = () => {
        const currentTime = Math.floor(Date.now() / 1000);
        if (currentTime > expirationTime) {
          console.log("Token expired during session, logging out user");
          // Set session invalid flag to show appropriate message
          sessionStorage.setItem("sessionInvalid", "true");
          dispatch("logout");
          // Redirect to login
          window.location.href = "/login";
        }
      };

      // Check every minute
      const intervalId = setInterval(checkExpiration, 60000);

      // Store interval ID to clear it on logout
      commit("SET_EXPIRATION_CHECK_INTERVAL", intervalId);
    },

    // Check if current token is expired (for testing)
    checkTokenExpiration({ state, dispatch }) {
      if (!state.token) return false;

      try {
        const base64Url = state.token.split(".")[1];
        const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
        const jsonPayload = decodeURIComponent(
          atob(base64)
            .split("")
            .map(function (c) {
              return "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2);
            })
            .join(""),
        );

        const decodedData = JSON.parse(jsonPayload);
        const currentTime = Math.floor(Date.now() / 1000);

        if (decodedData.exp && currentTime > decodedData.exp) {
          console.log("Token is expired, logging out user");
          dispatch("logout");
          return true;
        }

        return false;
      } catch (error) {
        console.error("Error checking token expiration:", error);
        return false;
      }
    },
  },
  getters: {
    isLoading: (state) => state.loading,
    hasError: (state) => state.error !== null,
    errorMessage: (state) => state.error,
    isAuthenticated: (state) => state.isAuthenticated,
    user: (state) => state.user,
    token: (state) => state.token,
    // Get authorization header for API requests
    authHeader: (state) => {
      return state.token ? { Authorization: `Bearer ${state.token}` } : {};
    },
    // Check if token is expired
    isTokenExpired: (state) => {
      if (!state.token) return true;

      try {
        const base64Url = state.token.split(".")[1];
        const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
        const jsonPayload = decodeURIComponent(
          atob(base64)
            .split("")
            .map(function (c) {
              return "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2);
            })
            .join(""),
        );

        const decodedData = JSON.parse(jsonPayload);
        const currentTime = Math.floor(Date.now() / 1000);

        return decodedData.exp && currentTime > decodedData.exp;
      } catch (error) {
        console.error("Error checking token expiration in getter:", error);
        return true;
      }
    },
    flags: (state) => state.flags || {},
    flagById: (state) => (id) => (state.flags || {})[id],
    isFlagLoaded: (state) => (id) => !!(state.flagsLoadFinished || {})[id],
  },
});
